import {ShowMore, type ShowMoreRef, type ShowMoreToggleLinesFn} from '@re-dev/react-truncate'

import LocationSvg from '@/assets/food/icons/location.svg?react'
import StartSvg from '@/assets/icons/star/full.svg?react'
import type {Store, UserInfo} from '@/lib/food/foodModel'
import {useSelectedStore} from '@/lib/food/store'
import {StoreDetailRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './StoreCard.module.scss'
import StoreImageFallback from './StoreImageFallback'

interface StoreCardProps {
  store: Store
  headingLevel?: number
  className?: string
  userInfo: UserInfo
}

export default memo(function StoreCard(props: StoreCardProps) {
  const {
    store,
    userInfo: {
      lat: userLat,
      lng: userLng,
      address: userAddress,
      user_street_num: userStressNum,
      user_street_name: userStressName,
      user_city: userCity,
      user_state: userState,
      user_zipcode: userZipcode,
      user_country: userCountry,
    },
  } = props

  const [_selectedStore, setSelectedStore] = useSelectedStore()

  const storeDetailParams = {
    id: store._id,
    store_name: store.name,
    store_description: store.description ?? '',
    store_rating: store.weighted_rating_value,
    store_rating_count: store.aggregated_rating_count,
    store_address: store.address,
    store_cuisine: store.cuisines,
    store_delivery_time:
      store.quotes?.cheapest_delivery?.time_estimate.minimum ??
      store.quotes?.pickup?.time_estimate.minimum ??
      0,
    store_delivery_distance: store.miles,
    store_image: store.logo_photos,
    lat: userLat,
    lng: userLng,
    address: userAddress,
    user_street_num: userStressNum,
    user_street_name: userStressName,
    user_city: userCity,
    user_state: userState,
    user_zipcode: userZipcode,
    user_country: userCountry,
    pickup_enabled: store.pickup_enabled,
    delivery_enabled: store.delivery_enabled,
  }

  const rating = store.weighted_rating_value ?? 0
  const formattedRating = rating > 0 ? rating.toFixed(1) : '0.0'

  const descriptionElement = useRef<HTMLDivElement>(null)

  const ref = useRef<ShowMoreRef>(null)

  const toggleLines: ShowMoreToggleLinesFn = e => {
    ref.current?.toggleLines(e)
  }

  const handleSaveSelectedStoreInformation = () => {
    setSelectedStore({
      store_id: store._id,
      store_name: store.name,
      store_description: store.description ?? '',
      store_rating: store.weighted_rating_value ?? 0,
      store_rating_count: store.aggregated_rating_count ?? 0,
      store_address: store.address,
      store_cuisine: store.cuisines,
      store_delivery_time:
        store.quotes?.cheapest_delivery?.time_estimate.minimum ??
        store.quotes?.pickup?.time_estimate.minimum ??
        0,
      store_delivery_distance: store.miles ?? 0,
      store_image: store.logo_photos,
      pickup_enabled: store.pickup_enabled,
      delivery_enabled: store.delivery_enabled,
    })
  }

  return (
    <div className={style.Store} role='listitem'>
      <Link
        to={StoreDetailRoute.fullPath}
        params={{
          storeId: storeDetailParams,
        }}
        onClick={handleSaveSelectedStoreInformation}
        referrerPolicy='no-referrer'
        className={style.StoreLink}
      >
        <VisuallyHidden as='div' role='heading' aria-level={props.headingLevel}>
          {store.name}
        </VisuallyHidden>
        <StoreImageFallback images={store.logo_photos}/>
      </Link>
      <div className={style.StoreContent}>
        <Link
          to={StoreDetailRoute.fullPath}
          params={{
            storeId: storeDetailParams,
          }}
          rel='noreferrer'
          tabIndex={-1}
          aria-hidden='true'
          className={style.StoreName}
        >
          <VisuallyHidden as='div' role='heading' aria-level={props.headingLevel}></VisuallyHidden>
          {store.name}
        </Link>
        <div className={style.StoreInfo}>
          <div className={style.StoreInfoDesc}>
            <VisuallyHidden>Store description: {store.description?.length === 0 && "Not available"}</VisuallyHidden>
            <div ref={descriptionElement}>
              <ShowMore
                ref={ref}
                lines={3}
                more={
                  <button onClick={toggleLines} className={style.ReadMore}>
                    Read more
                  </button>
                }
                less={
                  <button onClick={toggleLines} className={style.ReadMore}>
                    Read less
                  </button>
                }
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: store.description ?? '',
                  }}
                />
              </ShowMore>
            </div>
          </div>
          <div className={style.StoreInfoDetail}>
            {/* <div className={style.StoreDetailItem}>
              <div className={style.StoreDetailItemValue}>
                <VisuallyHidden>Delivery time: </VisuallyHidden>
                {`${(
                  store.quotes?.cheapest_delivery?.time_estimate.minimum ??
                  store.quotes?.pickup?.time_estimate.minimum ??
                  0
                ).toString()} mins`}
              </div>
            </div>
            <div className={style.StoreDetailItemBreak}></div> */}
            <div className={style.StoreDetailItem}>
              <div className={style.StoreDetailItemValue}>
                <VisuallyHidden>Delivery distance: </VisuallyHidden>
                {`< ${store.miles?.toFixed(3)} mi`}
              </div>
            </div>
            {rating > 0 && (
              <>
                {' '}
                <div className={style.StoreDetailItemBreak}></div>
                <div className={style.StoreDetailItem}>
                  <VisuallyHidden>{`Rating: ${formattedRating} out of 5 stars`}</VisuallyHidden>
                  <p aria-hidden='true' className={style.StoreDetailItemValue}>
                    {formattedRating}
                  </p>
                  <StartSvg className={style.StoreDetailItemIcon} />
                </div>
              </>
            )}
          </div>
          <div className={style.StoreInfoStatus}>
            <div
              className={clsx(
                style.StoreInfoStatusIcon,
                store.is_open ? style.Opened : style.Closed,
              )}
            ></div>
            <div className={style.StoreInfoStatusTitle}>
              <VisuallyHidden>Store status: </VisuallyHidden>
              {store.is_open ? 'Opened' : 'Closed'}
            </div>
          </div>
          <div className={style.StoreBreak}></div>
        </div>
        <div className={style.StroreAddress}>
          <LocationSvg className={style.StroreAddressIcon} />
          <div className={style.StroreAddressValue}>
            <VisuallyHidden>Store address: </VisuallyHidden>
            {`${store.address.street_addr}, ${store.address.city}, ${store.address.state}`}
          </div>
        </div>
      </div>
    </div>
  )
})
